/**
 * IaCondutaPage — domínio "IA & conduta" (`/admin/ia`), Admin Hub T8.
 *
 * O caso máximo do hub (dispatch da T8): 5 fontes de config hoje espalhadas em
 * 4 abas de `ConfigTenantPage` + 1 página inteira própria (`TenantAiConfigPage`,
 * 775 linhas), unificadas com gates visíveis numa página só. Duas extrações:
 *   - `ConfirmacaoCadastralSection` — miolo do `AiTab` interno de
 *     `ConfigTenantPage.tsx` (o `<Link to="/admin/ai-config">` morreu: essa
 *     rota já redireciona de volta para `/admin/ia#modelos`, T3).
 *   - `ModelosSection` — miolo de `TenantAiConfigPage.tsx` (775 linhas), gate
 *     de leitura duplicado removido (fica só o de `DomainSection`, via
 *     catálogo).
 *
 * `canManage` (`tenant-config-manage`) é resolvido AQUI, uma vez, e passado a
 * `ConductTab`/`BehavioralTab`/`SilentTab` — mesmo contrato que os 9 tabs de
 * `config-tenant/` já usam (medido em context.md). `ConfirmacaoCadastralSection`
 * também recebe `canManage` (mesmo campo de escrita), mas salva IMEDIATO — não
 * se registra na `StickySaveBar`. `ModelosSection` resolve a SUA PRÓPRIA
 * capability de escrita internamente (`custom-model-selection`, não
 * `tenant-config-manage` — é outra superfície) e também não se registra
 * (não há dirty-diff nessa tela: cada linha grava por ação).
 *
 * Fidelidade — prancha "02 Dominio IA e conduta (pagina padrao).dc.html",
 * variante 2a (a que a T4 e o TL já escolheram para o rail — ver docblock de
 * `DomainPage.tsx`). Esta é a ÚNICA das 5 páginas de domínio que a prancha
 * retrata de fato (as demais foram inferidas do padrão 2a genérico), então é
 * o teste de fidelidade mais direto do plano inteiro. Divergências DECLARADAS:
 *
 *   1. **A prancha mostra "Modelos & failover" LOCKED** (badge `Pro`, corpo
 *      genérico "hoje os agentes usam os defaults da plataforma"). O código
 *      reproduz exatamente esse estado quando falta `custom-model-selection`
 *      (via `CapabilityLockedBanner` do `DomainSection`) — mas a prancha não
 *      desenha NENHUM estado desbloqueado dessa seção, então não há
 *      referência visual para o corpo de `ModelosSection` quando a capability
 *      está presente. Implementado com o componente real (`ModelosSection`,
 *      775 linhas movidas), não uma reconstrução a partir da prancha.
 *
 *   2. **As outras 4 seções, na prancha, são linhas condensadas** (label +
 *      efeito + 1 controle à direita, no padrão `.item` de
 *      `design-references`) — aqui cada uma é o FORM REAL e completo
 *      (`ConductTab` com os 5 seletores + explicação de "herdar";
 *      `BehavioralTab` com o grid de 8 padrões; `SilentTab` com o editor
 *      dia-a-dia de horário comercial). Mesma divergência que a T7 já
 *      declarou para `BrandingSection` vs. a prancha 05 — reuso do
 *      componente real em vez de fabricar um resumo (que exigiria inventar
 *      contagens/frases "Hoje: X · definido aqui" sem fonte viva para a
 *      origem por-campo, algo que só a Fase 2 "Conduta e Contexto" resolve
 *      PARA OS 5 EIXOS de conduta via `axisResolution` — e mesmo esse dado
 *      não cobre `comportamental`/`acompanhamento-silencioso`).
 *
 *   3. **RESOLVIDA no fix round 1 (task-8-report.md) — não era só forma, era
 *      CONTEÚDO.** Na primeira versão desta task, a prancha e `hub/catalog.ts`
 *      descreviam o item `acompanhamento-silencioso` como **observação
 *      silenciosa de supervisor**. O coordenador MEDIU a origem: essa frase
 *      era, quase verbatim, a `description` da capability real de plataforma
 *      `silent-monitoring` (`packages/shared/src/capabilities/
 *      capabilities.spec.yaml:544`, ADR-094 — permissão PER-USER, tier Team+,
 *      categoria coordination). Um conceito de plataforma genuíno, mas
 *      DIFERENTE do que `SilentTab` (o componente que o dispatch da T8 mapeia
 *      a este id) implementa: horário comercial + auto-resposta fora de
 *      horário. `catalog.ts` foi corrigido (`label`/`efeito` agora descrevem
 *      o que `SilentTab` REALMENTE faz; pinado em `catalog.spec.ts`) — a
 *      mesma classe de erro registrada em CLAUDE.md v3.801 (uma palavra, dois
 *      significados). `id`/`dominio` do item NÃO mudaram (decisão do
 *      coordenador — ambos estruturais, viram item para o TL).
 *
 *   4. **RESOLVIDA no fix round 1 — `ConductTab`/`BehavioralTab`/`SilentTab`
 *      não mantêm mais header próprio.** Com o catálogo corrigido (item 3),
 *      a justificativa de manter um segundo título deixou de existir para as
 *      3 seções; todas seguem agora o mesmo padrão que `GeneralTab` já
 *      estabeleceu na T7 (título/descrição só em `DomainSection`, via
 *      `item.label`/`item.efeito`). O que era conteúdo genuinamente ausente
 *      do catálogo foi DOBRADO no próprio `item.efeito` em vez de virar um
 *      segundo heading: `comportamental` ganhou "e sinaliza ao supervisor".
 *      A semântica de "Herdar" de `ConductTab` não precisou de fold — já é
 *      visível por eixo em `ConductAxisField` (opção "Herdar — <valor>" +
 *      frase de origem). Um detalhe do header antigo de `SilentTab` foi
 *      deliberadamente NÃO dobrado ("a conversa fica registrada para
 *      atendimento prioritário" — mecânica de outro subsistema, não o que
 *      esta seção configura).
 *
 *   4-bis. **`conta-saldo` (IA como serviço S2, 16/Set) é seção NOVA, posterior
 *      à prancha** — nenhuma referência visual a comparar. Segue a regra de
 *      construção do hub (frente nova de produto = ITEM novo no catálogo,
 *      nunca aba/página nova, spec §3.4-5) e o molde de `ModelosSection`:
 *      somente leitura, gate de leitura só no `DomainSection`
 *      (`ai-billing-read`), cap de exportação (`ai-billing-manage`) resolvida
 *      dentro da seção. Entra ANTES de `modelos`: o dinheiro contextualiza a
 *      escolha de modelo que vem logo abaixo.
 *
 *   4-ter. **`base-de-conhecimento` (08/Out/2026) é seção NOVA, sem prancha** — mesma
 *      regra de construção do 4-bis (item novo no catálogo). Cap de PAPEL
 *      (`knowledge-base-author`, só admin_tenant) com `omitWhenBarred`: sem ela a seção
 *      some (rail e corpo). CRUD por ação (cada salvar grava) — não entra na
 *      `StickySaveBar`. Entra antes de `conta-saldo`: o que a IA sabe vem junto do como
 *      ela fala; o dinheiro e o modelo fecham a página. A seção também gateia o FETCH
 *      por conta própria (D-B).
 *
 *   5. A busca "Buscar nesta página…" do cabeçalho da prancha não existe — é
 *      parte do layout de `DomainPage` (T4), fora do escopo desta task (mesma
 *      nota que a T7 já registrou para a prancha 05).
 */
import { useCapability } from '../../../../hooks/use-capability';
import { ContaSaldoSection } from '../../ai-billing/ContaSaldoSection';
import { ModelosSection } from '../../ai-config/TenantAiConfigPage';
import { BehavioralTab } from '../../config-tenant/BehavioralTab';
import { ConductTab } from '../../config-tenant/ConductTab';
import { ConfirmacaoCadastralSection } from '../../config-tenant/ConfirmacaoCadastralSection';
import { SilentTab } from '../../config-tenant/SilentTab';
import { KnowledgeBaseSection } from '../../knowledge-base/KnowledgeBaseSection';
import { CONFIG_CATALOG } from '../catalog';
import { DomainPage } from '../DomainPage';
import { DomainSection } from '../DomainSection';
import { DomainSaveProvider } from '../save-bar-context';
import { StickySaveBar } from '../StickySaveBar';

function item(id: string) {
  const found = CONFIG_CATALOG.find((i) => i.id === id);
  if (found === undefined) throw new Error(`Item de catálogo ausente: ${id}`);
  return found;
}

export function IaCondutaPage(): JSX.Element {
  const canManage = useCapability('tenant-config-manage');

  return (
    <DomainSaveProvider>
      <DomainPage dominio="ia">
        <DomainSection item={item('conduta')}>
          <ConductTab canManage={canManage} />
        </DomainSection>
        <DomainSection item={item('confirmacao-cadastral')}>
          <ConfirmacaoCadastralSection canManage={canManage} />
        </DomainSection>
        <DomainSection item={item('comportamental')}>
          <BehavioralTab canManage={canManage} />
        </DomainSection>
        <DomainSection item={item('acompanhamento-silencioso')}>
          <SilentTab canManage={canManage} />
        </DomainSection>
        <DomainSection item={item('base-de-conhecimento')}>
          <KnowledgeBaseSection />
        </DomainSection>
        <DomainSection item={item('conta-saldo')}>
          <ContaSaldoSection />
        </DomainSection>
        <DomainSection item={item('modelos')}>
          <ModelosSection />
        </DomainSection>
        <StickySaveBar />
      </DomainPage>
    </DomainSaveProvider>
  );
}
